import { useRef, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useBrowserHarnessStream } from '@/hooks/useBrowserHarnessStream';
import { useAuthStore } from '@/stores/authStore';
import { useBrowserStore } from '@/stores/browserStore';
import { useSetupStore } from '@/stores/setupStore';
import { API_BASE } from '@/lib/config';

interface BrowserPanelProps {
  onClose: () => void;
  /** Visual mode:
   *  'full'    - dev chrome: URL bar, nav buttons, tabs, status bar (default, used in chat)
   *  'minimal' - viewport only, nothing that hints at an embedded browser
   *              (used on sign-in page so the user just sees Zoom content). */
  variant?: 'full' | 'minimal';
}

function scaleCoords(
  clientX: number,
  clientY: number,
  imgEl: HTMLImageElement,
  viewport: { width: number; height: number },
) {
  const rect = imgEl.getBoundingClientRect();
  // Map against the frame's real size — it IS Chrome's current viewport. The
  // store's viewport is only what THIS panel last asked for; another app tab
  // sharing the same Chrome may have resized it since.
  const width = imgEl.naturalWidth || viewport.width;
  const height = imgEl.naturalHeight || viewport.height;
  const scaleX = width / rect.width;
  const scaleY = height / rect.height;
  return {
    x: Math.round((clientX - rect.left) * scaleX),
    y: Math.round((clientY - rect.top) * scaleY),
  };
}

// Narrowest viewport the harness Chromium renders at. Below 1024px the Zoom
// Marketplace collapses its header into a hamburger menu — Sign in, Sign up,
// the Develop menu and the app search leave the DOM — and the guided-setup
// driver (backend/browser/intro-api.ts) can neither see the sign-in state nor
// reach the builder, so setup stalls (measured live 2026-10-02: full header
// at ≥1024, hamburger at ≤1000). A narrower panel gets a 1024-wide viewport
// at the panel's aspect ratio; the frame <img> scales it down uniformly and
// scaleCoords maps clicks back through the same ratio.
const MIN_VIEWPORT_WIDTH = 1024;

/** The viewport to request for a panel of `w`×`h` CSS pixels. */
function harnessViewport(w: number, h: number): { width: number; height: number } {
  if (w >= MIN_VIEWPORT_WIDTH) return { width: w, height: h };
  return { width: MIN_VIEWPORT_WIDTH, height: Math.round((h * MIN_VIEWPORT_WIDTH) / w) };
}

interface BrowserTab {
  url: string;
  title: string;
  isActive: boolean;
}

// Playful rotating labels for the "ZAC is driving" overlay (à la Claude's
// "Clauding…"). Cycled while ZAC controls the harness.
const ZAC_WORKING_LABELS = ['Zacking…', 'Wrangling Zoom…', 'Conjuring your app…'];

export function BrowserPanel({ onClose, variant = 'full' }: BrowserPanelProps) {
  const minimal = variant === 'minimal';
  const imgRef = useRef<HTMLImageElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const lastMouseMoveRef = useRef<number>(0);
  const lastResizeRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 });
  const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [urlInput, setUrlInput] = useState('');
  const [tabs, setTabs] = useState<BrowserTab[]>([]);
  const token = useAuthStore((s) => s.token);
  const setBrowserViewport = useBrowserStore((s) => s.setViewport);

  // Tab list refresh: on open and on every URL change — the harness sends
  // `status` on each main-frame navigation, which updates
  // browserStore.currentUrl. Panel hidden → no fetches at all.
  const panelOpen = useBrowserStore((s) => s.panelOpen);
  const disabled = useBrowserStore((s) => s.disabled);
  const agentControl = useBrowserStore((s) => s.agentControl);
  const userPaused = useBrowserStore((s) => s.userPaused);
  const setUserPaused = useBrowserStore((s) => s.setUserPaused);
  // A backend driver holds the harness tab's lease: the harness drops this
  // panel's input until it lets go or the user takes over, so the overlay
  // shows whatever userPaused says.
  const leasedBy = useBrowserStore((s) => s.leasedBy);
  const zacDriving = !!leasedBy || (agentControl && !userPaused);
  // Guided Zoom setup is active once an access level is chosen and we're not yet
  // connected. The take-over / resume control stays visible across this whole
  // window so it never vanishes when agentControl auto-toggles off mid-flow.
  const accessLevel = useSetupStore((s) => s.accessLevel);
  const setupConnected = useSetupStore((s) => s.connected);
  // While ZAC is waiting for the user to supply the app name, it's NOT driving —
  // so the overlay turns golden + "waiting for your input" instead of the blue
  // "ZAC is driving" hue.
  const awaitingAppName = useSetupStore((s) => s.awaitingAppName);
  // Step-bound status ("Reading your app credentials…") set by the flow that's
  // actually driving; the playful rotating labels are only the fallback.
  const drivingStatus = useBrowserStore((s) => s.drivingStatus);
  const runNote = useBrowserStore((s) => s.runNote);
  const inSetup = !!accessLevel && !setupConnected;
  const [workingLabelIdx, setWorkingLabelIdx] = useState(0);

  // Rotate the playful overlay label while ZAC is driving.
  useEffect(() => {
    if (!zacDriving) { setWorkingLabelIdx(0); return; }
    const id = setInterval(
      () => setWorkingLabelIdx((i) => (i + 1) % ZAC_WORKING_LABELS.length),
      2000,
    );
    return () => clearInterval(id);
  }, [zacDriving]);

  // "Take over" pauses ZAC's own flows (guided setup stands down on
  // userPaused) and breaks the harness lease, so a running change stops
  // before its next step and this panel's input is relayed again. "Let ZAC
  // continue" only unpauses: the next driver takes the lease itself.
  const takeOver = useCallback(async () => {
    setUserPaused(true);
    try {
      await fetch(`${API_BASE}/browser/session/take-over`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch { /* the harness drops the lease on its own when the driver stops heartbeating */ }
  }, [token, setUserPaused]);
  const canResume = userPaused && !zacDriving;
  const toggleControl = useCallback(() => {
    if (canResume) setUserPaused(false);
    else void takeOver();
  }, [canResume, setUserPaused, takeOver]);
  const currentBrowserUrl = useBrowserStore((s) => s.currentUrl);
  useEffect(() => {
    if (!panelOpen) return;
    let cancelled = false;
    const fetchTabs = async () => {
      try {
        const res = await fetch(`${API_BASE}/browser/tabs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok && !cancelled) {
          const data = await res.json();
          setTabs(data.tabs ?? []);
        }
      } catch { /* ignore */ }
    };
    fetchTabs();
    return () => { cancelled = true; };
  }, [token, panelOpen, currentBrowserUrl]);

  const {
    isConnected,
    currentUrl,
    latestFrame,
    fps,
    latency,
    viewport,
    send,
    connect,
    disconnect,
  } = useBrowserHarnessStream();

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    connect();
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  // One-shot: when something (e.g. guided Zoom setup) queues a navigation
  // target, drive the sidecar there once the session is actually ready, then
  // clear it so it fires only once.
  //
  // We gate on `currentUrl` (set by the sidecar's `init` message) rather than
  // just `isConnected`: the WS reports connected at `onopen`, but the auth
  // handshake + page-target attach only complete when `init` arrives. Firing
  // navigate before that drops the command — which is why the panel stuck on
  // about:blank.
  const pendingNavigateUrl = useBrowserStore((s) => s.pendingNavigateUrl);
  const setPendingNavigateUrl = useBrowserStore((s) => s.setPendingNavigateUrl);
  useEffect(() => {
    if (isConnected && currentUrl && pendingNavigateUrl) {
      send({ type: 'navigate', url: pendingNavigateUrl });
      setPendingNavigateUrl(null);
    }
  }, [isConnected, currentUrl, pendingNavigateUrl, send, setPendingNavigateUrl]);

  // Prime the store with the container's initial size before the WS even
  // opens, so the auth message can carry viewport dims and the very first
  // frame emits at the right resolution — no 1280x1024 flash.
  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const w = Math.round(el.clientWidth);
    const h = Math.round(el.clientHeight);
    if (w >= 320 && h >= 240) {
      const v = harnessViewport(w, h);
      setBrowserViewport(v);
      lastResizeRef.current = { w: v.width, h: v.height };
    }
  }, [setBrowserViewport]);

  // Keep the sidecar's CDP viewport in sync with this panel's on-screen size
  // so the streamed frames render edge-to-edge instead of scaled. Debounced
  // on the trailing edge so a window drag doesn't spam CDP round-trips —
  // only the final resting size actually lands on the backend.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el || !isConnected) return;

    // Reset the dedup marker on (re)connect so the first push is guaranteed
    // to reach the backend — a reconnect may hit a fresh sidecar that has
    // reverted to Chrome's default viewport.
    lastResizeRef.current = { w: 0, h: 0 };

    const push = () => {
      // CSS pixels, no devicePixelRatio multiplier: Retina-doubling would
      // push a very large viewport to Chrome, which some sites interpret as
      // "big display" and render at an unusual scale.
      const w = Math.round(el.clientWidth);
      const h = Math.round(el.clientHeight);
      if (w < 320 || h < 240) return;
      const v = harnessViewport(w, h);
      if (Math.abs(v.width - lastResizeRef.current.w) < 8 && Math.abs(v.height - lastResizeRef.current.h) < 8) return;
      lastResizeRef.current = { w: v.width, h: v.height };
      setBrowserViewport(v);
      send({ type: 'resize', width: v.width, height: v.height });
    };

    const pushDebounced = () => {
      if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
      resizeTimerRef.current = setTimeout(push, 150);
    };

    // Fire once immediately on connect so the sidecar picks up the current
    // size right away — claiming the viewport first if this is the tab the
    // user is in. Further observer callbacks go through the debounce.
    if (document.visibilityState === 'visible' && document.hasFocus()) send({ type: 'focus' });
    push();
    const ro = new ResizeObserver(pushDebounced);
    ro.observe(el);
    // Several app tabs share one Chrome. The harness lets only the tab in
    // use resize it — the one that last sent input or `focus` — so a
    // background tab's ResizeObserver can't shrink the view you're using.
    // When this tab comes back into use, claim the viewport, then size it.
    // A tab that doesn't own it keeps rendering the owner's frames, scaled
    // through the image's natural size (scaleCoords).
    const reassert = () => {
      if (document.visibilityState !== 'visible') return;
      send({ type: 'focus' });
      lastResizeRef.current = { w: 0, h: 0 };
      push();
    };
    window.addEventListener('focus', reassert);
    document.addEventListener('visibilitychange', reassert);
    return () => {
      window.removeEventListener('focus', reassert);
      document.removeEventListener('visibilitychange', reassert);
      ro.disconnect();
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
        resizeTimerRef.current = null;
      }
    };
  }, [isConnected, send, setBrowserViewport]);

  // Sync URL input with current URL from stream
  useEffect(() => {
    setUrlInput(currentUrl);
  }, [currentUrl]);

  // Click handler
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLImageElement>) => {
      if (!imgRef.current) return;
      const { x, y } = scaleCoords(e.clientX, e.clientY, imgRef.current, viewport);
      send({ type: 'click', x, y });
    },
    [send, viewport],
  );

  // Double click handler
  const handleDoubleClick = useCallback(
    (e: React.MouseEvent<HTMLImageElement>) => {
      if (!imgRef.current) return;
      const { x, y } = scaleCoords(e.clientX, e.clientY, imgRef.current, viewport);
      send({ type: 'dblclick', x, y });
    },
    [send, viewport],
  );

  // Scroll handler
  const handleWheel = useCallback(
    (e: React.WheelEvent<HTMLImageElement>) => {
      e.preventDefault();
      if (!imgRef.current) return;
      const { x, y } = scaleCoords(e.clientX, e.clientY, imgRef.current, viewport);
      send({
        type: 'scroll',
        x,
        y,
        deltaX: Math.round(e.deltaX),
        deltaY: Math.round(e.deltaY),
      });
    },
    [send, viewport],
  );

  // Mouse move handler (throttled to 20Hz)
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLImageElement>) => {
      const now = performance.now();
      if (now - lastMouseMoveRef.current < 50) return;
      lastMouseMoveRef.current = now;

      if (!imgRef.current) return;
      const { x, y } = scaleCoords(e.clientX, e.clientY, imgRef.current, viewport);
      send({ type: 'mousemove', x, y });
    },
    [send, viewport],
  );

  // Keyboard handler (when image is focused via tabIndex)
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Let browser handle tab for accessibility
      if (e.key === 'Tab') return;

      e.preventDefault();

      // Single printable character -- send as type event
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        send({ type: 'type', text: e.key });
      } else {
        send({ type: 'keydown', key: e.key });
      }
    },
    [send],
  );

  // URL navigation
  const handleUrlSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!urlInput.trim()) return;

      let url = urlInput.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = `https://${url}`;
      }
      send({ type: 'navigate', url });
    },
    [send, urlInput],
  );

  // The tab's own history: a pressed Alt+Arrow over CDP is not the browser's shortcut (the harness's back/forward/reload).
  const handleBack = useCallback(() => {
    send({ type: 'back' });
  }, [send]);

  const handleForward = useCallback(() => {
    send({ type: 'forward' });
  }, [send]);

  const handleReload = useCallback(() => {
    send({ type: 'reload' });
  }, [send]);

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Top bar with URL and controls */}
      {!minimal && (
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-card">
        {/* Navigation buttons */}
        <div className="flex items-center gap-1">
          <button
            onClick={handleBack}
            className="p-1.5 rounded text-muted-foreground hover:bg-secondary hover:text-foreground"
            title="Back"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={handleForward}
            className="p-1.5 rounded text-muted-foreground hover:bg-secondary hover:text-foreground"
            title="Forward"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <button
            onClick={handleReload}
            className="p-1.5 rounded text-muted-foreground hover:bg-secondary hover:text-foreground"
            title="Reload"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>

        {/* URL bar */}
        <form onSubmit={handleUrlSubmit} className="flex-1">
          <input
            type="text"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="Enter URL..."
            className="w-full px-3 py-1.5 text-sm bg-secondary rounded border border-border text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </form>

        {/* Take-over / resume — lives in ZAC's own chrome (not floating over the
            page, where it collided with Zoom's nav). Persistent through the whole
            guided setup so it never vanishes when agentControl auto-toggles. */}
        {(inSetup || zacDriving || userPaused) && (
          <button
            type="button"
            onClick={toggleControl}
            className={
              canResume
                ? 'shrink-0 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-500 cursor-pointer'
                : 'shrink-0 rounded-md border border-blue-500/50 bg-blue-500/10 px-3 py-1.5 text-xs font-semibold text-blue-400 hover:bg-blue-500/20 cursor-pointer'
            }
          >
            {canResume ? 'Let ZAC continue' : 'Take over'}
          </button>
        )}

        {/* Close button */}
        <button
          onClick={onClose}
          className="p-1.5 rounded text-muted-foreground hover:bg-secondary hover:text-foreground"
          title="Close browser panel"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      )}

      {/* Tab bar */}
      {!minimal && tabs.length > 1 && (
        <div className="flex items-center gap-0.5 px-2 py-1 border-b border-border bg-card overflow-x-auto">
          {tabs.map((tab, i) => {
            const label = tab.url.replace(/^https?:\/\//, '').split(/[?#]/)[0]?.substring(0, 40) || 'New Tab';
            return (
              <button
                key={i}
                onClick={async () => {
                  if (tab.isActive) return;
                  try {
                    await fetch(`${API_BASE}/browser/tabs/switch`, {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ index: i }),
                    });
                  } catch { /* ignore */ }
                }}
                className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded truncate max-w-[200px] cursor-pointer ${
                  tab.isActive
                    ? 'bg-secondary text-foreground'
                    : 'text-muted-foreground hover:bg-secondary/50'
                }`}
                title={tab.url}
              >
                <span className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                  tab.isActive ? 'bg-blue-500' : 'bg-muted-foreground/30'
                }`} />
                <span className="truncate">{label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Viewport area */}
      <div
        ref={viewportRef}
        className={`relative flex-1 overflow-hidden bg-black ${minimal ? '' : 'flex items-center justify-center'}`}
      >
        {latestFrame ? (
          <img
            ref={imgRef}
            src={latestFrame}
            alt=""
            className={`block w-full h-full ${minimal ? 'cursor-default' : 'cursor-crosshair'}`}
            tabIndex={0}
            onClick={handleClick}
            onDoubleClick={handleDoubleClick}
            onWheel={handleWheel}
            onMouseMove={handleMouseMove}
            onKeyDown={handleKeyDown}
            draggable={false}
          />
        ) : minimal ? (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3">
            <div className="w-6 h-6 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
            <div className="text-xs text-muted-foreground">
              {isConnected ? 'Connected — waiting for frames…' : 'Connecting to browser…'}
            </div>
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
            {isConnected ? 'Waiting for frames...' : 'Connecting...'}
          </div>
        )}

        {/* Disabled gate: blur + block interaction until the user completes the
            prior step (e.g. choosing an access level during guided setup). */}
        {disabled && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 backdrop-blur-sm cursor-not-allowed">
            <span className="rounded-full bg-black/70 px-4 py-2 text-sm font-medium text-white/90">
              Choose an access level to continue
            </span>
          </div>
        )}

        {/* Agent control: blue "ZAC is driving" hue while ZAC controls the
            harness — OR a golden "waiting for your input" hue when ZAC has
            paused to ask the user something (the app-name step). Both block
            page manipulation; hidden the moment the user takes over, except
            while a driver still holds the lease (the harness is ignoring
            this panel's input until it lets go). The pill anchors
            top-center so it never covers the content being read. */}
        {(zacDriving || (awaitingAppName && !userPaused)) && (
          <div className={`absolute inset-0 z-20 flex items-start justify-center pt-4 cursor-not-allowed ${awaitingAppName && !zacDriving ? 'bg-amber-500/20' : 'bg-blue-500/20'}`}>
            <span className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-white shadow-lg ${awaitingAppName && !zacDriving ? 'bg-amber-600/90' : 'bg-blue-600/85'}`}>
              {awaitingAppName && !zacDriving ? (
                <>
                  <span className="h-2 w-2 rounded-full bg-white/90 animate-pulse" />
                  Waiting for your input…
                </>
              ) : (
                <>
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  {drivingStatus ?? ZAC_WORKING_LABELS[workingLabelIdx]}
                </>
              )}
            </span>
          </div>
        )}

        {/* A plan's run on another channel: the step ZAC is on, over the panel, which stays the user's. */}
        {!zacDriving && runNote !== null && (
          <div className="pointer-events-none absolute inset-x-0 top-4 z-20 flex justify-center">
            <span className="flex items-center gap-2 rounded-full bg-slate-800/85 px-4 py-2 text-sm font-medium text-white shadow-lg">
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
              {runNote}
            </span>
          </div>
        )}

        {/* Take-over control in the minimal variant only — no chrome row exists
            there to host it. The full variant renders it in the top bar so it
            never collides with the page's own UI. */}
        {minimal && (inSetup || zacDriving || userPaused) && (
          <button
            type="button"
            onClick={toggleControl}
            className={
              canResume
                ? 'absolute top-3 right-3 z-30 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-lg hover:bg-blue-500 cursor-pointer'
                : 'absolute top-3 right-3 z-30 rounded-md bg-white/90 px-3 py-1.5 text-xs font-semibold text-blue-700 shadow hover:bg-white cursor-pointer'
            }
          >
            {canResume ? 'Let ZAC continue' : 'Take over'}
          </button>
        )}
      </div>

      {/* Status bar */}
      {!minimal && (
      <div className="flex items-center justify-between px-3 py-1.5 border-t border-border bg-card text-xs text-muted-foreground">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5">
            <span
              className={`inline-block w-2 h-2 rounded-full ${
                isConnected ? 'bg-green-500' : 'bg-red-500'
              }`}
            />
            {isConnected ? 'Connected' : 'Disconnected'}
          </span>
          {isConnected && (
            <>
              <span>{fps} fps</span>
              <span>{latency}ms</span>
            </>
          )}
        </div>
        <span>
          {viewport.width}x{viewport.height}
        </span>
      </div>
      )}
    </div>
  );
}
